import type { ConversationSearchMatcher } from '../../types/conversation-search-refresh.types';
import type { QueryMatcherRetention } from '../../types/query-matcher-retention.types';

/**
 * Expires captured event snapshots independently of transport completion.
 * Each notification has its own finite admission window; later notifications
 * cannot extend the lifetime of older predicates.
 * @returns Predicate collection with automatic expiry and explicit cleanup.
 */
export const createQueryMatcherRetention = (): QueryMatcherRetention => {
  const entries = new Map<ConversationSearchMatcher, number>();
  let timer: number | undefined;
  const expire = (): void => {
    window.clearTimeout(timer);
    timer = undefined;
    const now = Date.now();
    let next = Infinity;
    entries.forEach((deadline, matcher) => {
      if (deadline <= now) {
        entries.delete(matcher);
      } else {
        next = Math.min(next, deadline);
      }
    });
    if (Number.isFinite(next)) {
      timer = window.setTimeout(expire, next - now);
    }
  };
  return {
    add: (matcher) => {
      entries.set(matcher, Date.now() + 30_000);
      expire();
    },
    matches: (query) => {
      expire();
      return [...entries.keys()].some((matcher) => matcher(query));
    },
    clear: () => {
      window.clearTimeout(timer);
      timer = undefined;
      entries.clear();
    },
  };
};
